/**
 * CustomerListCard - 고객 리스트 카드 컴포넌트
 *
 * 기존 인라인 CustomerListItem + CustomerHoverContent 추출
 * CRM 확장 슬롯 (tags, tier) 포함
 */

import { useState } from "react";
import {
  Star,
  StarOff,
  Phone,
  MoreVertical,
  Cake,
  FileText,
  User,
  Clock,
  MessageSquare,
  PhoneCall,
  Calendar,
  Pencil,
  Ban,
  CalendarCheck,
  AlertTriangle,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatBirthDate, getDisplayName } from "@/components/customer";
import ClickablePhoneNumber from "@/components/common/ClickablePhoneNumber";
import { customerApi } from "@/api/customer";
import { queryKeys } from "@/lib/queryKeys";
import type { Customer } from "@/types/customer";
import type { Consult } from "@/types/consult";

// 상대 시간 표시
function getRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return "오늘";
  if (days === 1) return "어제";
  if (days < 7) return `${days}일 전`;
  if (days < 30) return `${Math.floor(days / 7)}주 전`;
  if (days < 365) return `${Math.floor(days / 30)}개월 전`;
  return `${Math.floor(days / 365)}년 전`;
}

// 정확한 날짜 표시 (YYYY.MM.DD)
function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

// 통화 시간 포맷 (초 → "X시간 Y분 (Z분)" 또는 "Xm")
// 1시간 이상이면 총 분을 항상 보조 표시 (재계산 불편 회피)
function formatDuration(seconds: number): string {
  if (seconds <= 0) return "0분";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) {
    const hourPart = m > 0 ? `${h}시간 ${m}분` : `${h}시간`;
    return `${hourPart} (${h * 60 + m}분)`;
  }
  return `${m}분`;
}

function getNotesCount(customer: Customer): number {
  return customer.memoCount ?? 0;
}

function getMemoPreview(customer: Customer): string | null {
  return customer.latestMemoContent || null;
}

// 등급 배지 색상
const TIER_COLORS: Record<string, string> = {
  VIP: "bg-purple-100 text-purple-700 border-purple-200",
  GOLD: "bg-amber-100 text-amber-700 border-amber-200",
  SILVER: "bg-muted text-muted-foreground border-border",
  BRONZE: "bg-warning/15 text-warning border-warning/30",
};

// HoverCard 내부 콘텐츠 (lazy loading)
function CustomerHoverContent({
  customer,
  isOpen,
}: {
  customer: Customer;
  isOpen: boolean;
}) {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: queryKeys.customers.stats(customer.id),
    queryFn: () => customerApi.getCustomerStats(customer.id),
    enabled: isOpen,
    staleTime: 30000,
  });

  const { data: consultsData, isLoading: consultsLoading } = useQuery({
    queryKey: queryKeys.consults.customerPaged(customer.id, 0),
    queryFn: () => customerApi.getCustomerConsults(customer.id, 0, 3),
    enabled: isOpen,
    staleTime: 30000,
  });

  const recentConsults = consultsData?.content || [];

  return (
    <div className="space-y-3">
      {/* 고객 기본 정보 */}
      <div className="space-y-1">
        <h4 className="font-semibold text-sm flex items-center gap-1.5">
          {getDisplayName(customer).name}
          {getDisplayName(customer).isAi && (
            <Badge
              variant="outline"
              className="text-[9px] px-1 py-0 text-violet-600 border-violet-200 bg-violet-50"
            >
              AI
            </Badge>
          )}
          {customer.starred && (
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
          )}
        </h4>
        <div className="text-xs text-muted-foreground space-y-0.5">
          <ClickablePhoneNumber
            phone={customer.phone}
            className="text-xs text-muted-foreground font-mono"
          />
          {customer.birthDate && (
            <div className="flex items-center gap-1.5">
              <Cake className="h-3 w-3" />
              <span>{formatBirthDate(customer.birthDate)}</span>
            </div>
          )}
        </div>
      </div>

      {/* 상담 통계 */}
      <div className="h-px w-full bg-border" />
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-muted-foreground mb-0.5">
            <PhoneCall className="h-3 w-3" />
            <span>상담</span>
          </div>
          {statsLoading ? (
            <Skeleton className="h-4 w-8 mx-auto" />
          ) : (
            <p className="font-semibold text-primary">
              {stats?.totalCallCount || 0}회
            </p>
          )}
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-muted-foreground mb-0.5">
            <Calendar className="h-3 w-3" />
            <span>방문</span>
          </div>
          {statsLoading ? (
            <Skeleton className="h-4 w-8 mx-auto" />
          ) : (
            <p className="font-semibold text-success">
              {stats?.visitCount || 0}회
            </p>
          )}
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-muted-foreground mb-0.5">
            <MessageSquare className="h-3 w-3" />
            <span>메모</span>
          </div>
          <p className="font-semibold">{getNotesCount(customer)}개</p>
        </div>
      </div>

      {/* 최근 상담 이력 */}
      {!consultsLoading && recentConsults.length > 0 && (
        <>
          <div className="h-px w-full bg-border" />
          <div className="space-y-1.5">
            <h5 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <PhoneCall className="h-3 w-3" />
              최근 상담
            </h5>
            <div className="space-y-1">
              {recentConsults.slice(0, 2).map((consult: Consult) => (
                <div
                  key={consult.id}
                  className="text-xs p-1.5 bg-muted/50 rounded flex items-center justify-between"
                >
                  <span className="text-muted-foreground">
                    {new Date(consult.occurredAt).toLocaleDateString("ko-KR", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <span
                    className={cn(
                      "px-1.5 py-0.5 rounded text-[10px] font-medium",
                      consult.result === "SUCCESS" &&
                        "bg-success/15 text-success",
                      consult.result === "FAIL" &&
                        "bg-destructive/10 text-destructive",
                      consult.result === "PENDING" &&
                        "bg-warning/15 text-warning",
                    )}
                  >
                    {consult.result === "SUCCESS"
                      ? "성공"
                      : consult.result === "FAIL"
                        ? "실패"
                        : "대기"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* 메모 미리보기 */}
      {customer.latestMemoContent && (
        <>
          <div className="h-px w-full bg-border" />
          <div className="space-y-1">
            <h5 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <FileText className="h-3 w-3" />
              메모
            </h5>
            <p className="text-xs text-foreground line-clamp-2 whitespace-pre-wrap">
              {customer.latestMemoContent}
            </p>
          </div>
        </>
      )}

      <p className="text-[10px] text-muted-foreground/70 pt-1">
        클릭하여 고객 상세 정보 보기
      </p>
    </div>
  );
}

// 메인 컴포넌트
export interface CustomerListCardProps {
  customer: Customer;
  onSelect: (customer: Customer) => void;
  onToggleStar: (id: number) => void;
  onEdit: (customer: Customer) => void;
  onDelete: (customer: Customer) => void;
  isSelected: boolean;
  isChecked?: boolean;
  onCheck?: (id: number, checked: boolean) => void;
}

export function CustomerListCard({
  customer,
  onSelect,
  onToggleStar,
  onEdit,
  onDelete,
  isSelected,
  isChecked = false,
  onCheck,
}: CustomerListCardProps) {
  const notesCount = getNotesCount(customer);
  const notePreview = getMemoPreview(customer);
  const [isHoverOpen, setIsHoverOpen] = useState(false);

  return (
    <div
      className={cn(
        "p-4 border-b hover:bg-muted/50 cursor-pointer transition-colors",
        isSelected && "bg-muted/40 border-l-2 border-l-primary",
        isChecked && "bg-primary/5",
      )}
      onClick={() => onSelect(customer)}
    >
      <div className="flex items-start gap-4">
        {/* 체크박스 */}
        {onCheck && (
          <div
            className="flex-shrink-0 flex items-center mt-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={isChecked}
              onCheckedChange={(checked) =>
                onCheck(customer.id, checked === true)
              }
              aria-label={`${customer.name} 선택`}
            />
          </div>
        )}

        {/* 아바타 */}
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-primary" />
        </div>

        {/* 메인 정보 영역 */}
        <div className="flex-1 min-w-0">
          {/* 1행: 이름 + 별 + 등급배지 + 전화번호 + 상담/방문 배지 */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <HoverCard
              openDelay={300}
              closeDelay={100}
              onOpenChange={setIsHoverOpen}
            >
              <HoverCardTrigger asChild>
                <div className="flex items-center gap-1.5 cursor-pointer">
                  <span className="font-semibold text-base hover:text-primary transition-colors">
                    {getDisplayName(customer).name}
                  </span>
                  {getDisplayName(customer).isAi && (
                    <Badge
                      variant="outline"
                      className="text-[9px] px-1 py-0 text-violet-600 border-violet-200 bg-violet-50"
                    >
                      AI
                    </Badge>
                  )}
                  {customer.starred && (
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                  )}
                </div>
              </HoverCardTrigger>
              <HoverCardContent className="w-80" side="right" align="start">
                <CustomerHoverContent
                  customer={customer}
                  isOpen={isHoverOpen}
                />
              </HoverCardContent>
            </HoverCard>

            {/* CRM: 등급 배지 */}
            {customer.tier && (
              <Badge
                variant="outline"
                className={cn(
                  "text-[10px] px-1.5 py-0",
                  TIER_COLORS[customer.tier],
                )}
              >
                {customer.tier}
              </Badge>
            )}

            <ClickablePhoneNumber
              phone={customer.phone}
              className="text-sm text-muted-foreground"
            />

            {/* 성별 */}
            {customer.gender && (
              <span className="text-xs text-muted-foreground">
                {customer.gender === "MALE"
                  ? "남"
                  : customer.gender === "FEMALE"
                    ? "여"
                    : ""}
              </span>
            )}

            {/* 통계 배지: 상담/방문/노쇼 */}
            {customer.totalCallCount != null && (
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0 font-normal"
              >
                <PhoneCall className="w-3 h-3 mr-0.5" />
                상담 {customer.totalCallCount}회
                {(customer.totalCallDurationSeconds ?? 0) > 0 && (
                  <span className="ml-0.5 text-muted-foreground">
                    ({formatDuration(customer.totalCallDurationSeconds!)})
                  </span>
                )}
              </Badge>
            )}
            {customer.visitCount != null && (
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0 font-normal text-success"
              >
                <CalendarCheck className="w-3 h-3 mr-0.5" />
                방문 {customer.visitCount}회
              </Badge>
            )}
            {(customer.noShowCount ?? 0) > 0 && (
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0 font-normal text-warning bg-warning/10"
              >
                <AlertTriangle className="w-3 h-3 mr-0.5" />
                노쇼 {customer.noShowCount}회
              </Badge>
            )}

            {/* 다음 예약 */}
            {customer.nextAppointmentDate && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 font-normal text-info border-info/30"
              >
                <Calendar className="w-3 h-3 mr-0.5" />
                다음예약{" "}
                {new Date(customer.nextAppointmentDate).toLocaleDateString(
                  "ko-KR",
                  { month: "numeric", day: "numeric" },
                )}
              </Badge>
            )}

            {/* 예약 차단 */}
            {customer.bookingBlocked && (
              <Badge
                variant="destructive"
                className="text-[10px] px-1.5 py-0 font-normal"
              >
                <Ban className="w-3 h-3 mr-0.5" />
                예약차단
              </Badge>
            )}
          </div>

          {/* 2행: 생년월일 + 메모개수 + 등록일 + 최근상담일 */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-1 flex-wrap">
            {customer.birthDate && (
              <div className="flex items-center gap-1">
                <Cake className="w-3.5 h-3.5" />
                <span>{formatBirthDate(customer.birthDate)}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <MessageSquare className="w-3.5 h-3.5" />
              <span>메모 {notesCount}개</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>
                등록 {formatDate(customer.createdAt)}{" "}
                <span className="text-muted-foreground/60">
                  ({getRelativeTime(customer.createdAt)})
                </span>
              </span>
            </div>
            {customer.lastCallDate && (
              <div className="flex items-center gap-1">
                <PhoneCall className="w-3.5 h-3.5" />
                <span>최근상담 {getRelativeTime(customer.lastCallDate)}</span>
              </div>
            )}
            {customer.lastInteractionAt && (
              <div className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                <span>
                  최근활동 {getRelativeTime(customer.lastInteractionAt)}
                </span>
              </div>
            )}
            {customer.lifecycleStage && (
              <Badge
                variant="outline"
                className={cn(
                  "text-[10px] px-1.5 py-0",
                  customer.lifecycleStage === "VIP" &&
                    "text-purple-700 border-purple-200 bg-purple-50",
                  customer.lifecycleStage === "CUSTOMER" &&
                    "text-success border-success/30 bg-success/10",
                  customer.lifecycleStage === "PROSPECT" &&
                    "text-muted-foreground border-border bg-muted",
                  customer.lifecycleStage === "CHURNED" &&
                    "text-destructive border-destructive/30 bg-destructive/10",
                )}
              >
                {customer.lifecycleStage === "VIP"
                  ? "VIP"
                  : customer.lifecycleStage === "CUSTOMER"
                    ? "고객"
                    : customer.lifecycleStage === "PROSPECT"
                      ? "잠재"
                      : customer.lifecycleStage === "CHURNED"
                        ? "휴면"
                        : customer.lifecycleStage}
              </Badge>
            )}
          </div>

          {/* CRM: 태그 칩 */}
          {customer.tags && customer.tags.length > 0 && (
            <div className="flex items-center gap-1 mb-1 flex-wrap">
              {customer.tags.slice(0, 3).map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="text-[10px] px-1.5 py-0 text-muted-foreground"
                >
                  #{tag}
                </Badge>
              ))}
              {customer.tags.length > 3 && (
                <span className="text-[10px] text-muted-foreground">
                  +{customer.tags.length - 3}
                </span>
              )}
            </div>
          )}

          {/* 3행: 메모 미리보기 (2줄 표시, 호버 시 전체 표시) */}
          {notePreview && (
            <TooltipProvider>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <div className="flex items-start gap-1.5 mt-1 p-1.5 rounded bg-muted/30 hover:bg-muted/50 transition-colors">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-foreground/80 line-clamp-2 text-left whitespace-pre-wrap">
                        {notePreview}
                      </p>
                      {notesCount > 1 && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          +{notesCount - 1}개 더 보기
                        </p>
                      )}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="max-w-sm max-h-60 overflow-y-auto"
                >
                  <div className="space-y-2">
                    <p className="text-sm break-words whitespace-pre-wrap">
                      {notePreview}
                    </p>
                    {customer.latestMemoCreatedAt && (
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(
                          customer.latestMemoCreatedAt,
                        ).toLocaleDateString("ko-KR")}
                      </p>
                    )}
                    {notesCount > 1 && (
                      <p className="text-[10px] text-muted-foreground">
                        고객 상세에서 전체 {notesCount}개 메모 확인
                      </p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* 액션 버튼 */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(customer);
                  }}
                >
                  <Pencil className="w-4 h-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>수정</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              onToggleStar(customer.id);
            }}
          >
            {customer.starred ? (
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            ) : (
              <StarOff className="w-4 h-4 text-muted-foreground" />
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(customer)}>
                수정
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => onDelete(customer)}
              >
                삭제
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
